// Admin API client. Supports two auth modes for backwards compatibility:
//
//   1. JWT (preferred) — set via authLogin(email, password). Sent as
//      Authorization: Bearer <token>. Survives reload via sessionStorage.
//
//   2. Legacy password gate — the original X-Admin-Password header. Kept as
//      a super-admin "emergency backdoor" matching the backend ADMIN_PASSWORD
//      env var. The old <LoginPage /> still uses this path.
//
// Every request includes whichever credential is currently active.
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PWD_KEY = "dc_admin_pwd_v1";
const JWT_KEY = "dc_admin_jwt_v1";
const USER_KEY = "dc_admin_user_v1";

// ---- token + password persistence ----

export function getAdminToken() {
  try {
    return sessionStorage.getItem(JWT_KEY) || "";
  } catch {
    return "";
  }
}
export function setAdminToken(token) {
  try {
    if (token) sessionStorage.setItem(JWT_KEY, token);
    else sessionStorage.removeItem(JWT_KEY);
  } catch {
    /* no-op */
  }
}
export function getAdminUser() {
  try {
    const raw = sessionStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
export function setAdminUser(user) {
  try {
    if (user) sessionStorage.setItem(USER_KEY, JSON.stringify(user));
    else sessionStorage.removeItem(USER_KEY);
  } catch {
    /* no-op */
  }
}
export function getAdminPassword() {
  try {
    return sessionStorage.getItem(PWD_KEY) || "";
  } catch {
    return "";
  }
}
export function setAdminPassword(pwd) {
  try {
    if (pwd) sessionStorage.setItem(PWD_KEY, pwd);
    else sessionStorage.removeItem(PWD_KEY);
  } catch {
    /* no-op */
  }
}

function authHeaders() {
  const jwt = getAdminToken();
  if (jwt) return { Authorization: `Bearer ${jwt}` };
  const pwd = getAdminPassword();
  return pwd ? { "X-Admin-Password": pwd } : {};
}

// ---- login / logout ----

export async function adminLogin(password) {
  // Legacy password gate. Treated as a super-admin emergency backdoor.
  const res = await axios.post(`${API}/admin/login`, { password });
  if (res.data?.ok) {
    setAdminPassword(password);
    setAdminToken("");
    setAdminUser({
      name: "Super-admin",
      email: "owner@dotconverse.com",
      role: "owner",
      permissions: ["leads", "blog", "cases", "settings", "users"],
      legacy_backdoor: true,
    });
    return true;
  }
  return false;
}

export async function authLogin(email, password) {
  const res = await axios.post(`${API}/admin/auth/login`, { email, password });
  if (res.data?.token) {
    setAdminToken(res.data.token);
    setAdminUser(res.data.user || null);
    setAdminPassword("");
    return res.data.user;
  }
  return null;
}

export function adminLogout() {
  setAdminPassword("");
  setAdminToken("");
  setAdminUser(null);
}

export async function fetchMe() {
  const res = await axios.get(`${API}/admin/auth/me`, { headers: authHeaders() });
  setAdminUser(res.data);
  return res.data;
}

export async function changeOwnPassword(current_password, new_password) {
  const res = await axios.post(
    `${API}/admin/auth/change-password`,
    { current_password, new_password },
    { headers: authHeaders() }
  );
  return res.data;
}

// ---- permissions helpers ----

export function hasSection(section) {
  const user = getAdminUser();
  if (!user) return false;
  if (user.role === "owner") return true;
  return Array.isArray(user.permissions) && user.permissions.includes(section);
}

// ---- existing CRUD calls ----

export async function fetchStats() {
  const res = await axios.get(`${API}/admin/stats`, { headers: authHeaders() });
  return res.data;
}

export async function fetchLeads(type) {
  const params = type ? { type } : {};
  const res = await axios.get(`${API}/admin/leads`, {
    headers: authHeaders(),
    params,
  });
  return res.data;
}

export async function deleteLead(id) {
  return axios.delete(`${API}/admin/leads/${id}`, { headers: authHeaders() });
}

export async function fetchBlog() {
  const res = await axios.get(`${API}/admin/blog`, { headers: authHeaders() });
  return res.data;
}

export async function createBlog(data) {
  const res = await axios.post(`${API}/admin/blog`, data, { headers: authHeaders() });
  return res.data;
}

export async function updateBlog(id, data) {
  const res = await axios.put(`${API}/admin/blog/${id}`, data, { headers: authHeaders() });
  return res.data;
}

export async function deleteBlog(id) {
  return axios.delete(`${API}/admin/blog/${id}`, { headers: authHeaders() });
}

export async function fetchCases() {
  const res = await axios.get(`${API}/admin/case-studies`, { headers: authHeaders() });
  return res.data;
}

export async function createCase(data) {
  const res = await axios.post(`${API}/admin/case-studies`, data, { headers: authHeaders() });
  return res.data;
}

export async function updateCase(id, data) {
  const res = await axios.put(`${API}/admin/case-studies/${id}`, data, { headers: authHeaders() });
  return res.data;
}

export async function deleteCase(id) {
  return axios.delete(`${API}/admin/case-studies/${id}`, { headers: authHeaders() });
}

export async function uploadImage(file, onProgress) {
  const form = new FormData();
  form.append("file", file);
  const res = await axios.post(`${API}/admin/upload`, form, {
    headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
    },
  });
  const rel = res.data?.url || "";
  return {
    ...res.data,
    absoluteUrl: rel.startsWith("http") ? rel : `${process.env.REACT_APP_BACKEND_URL}${rel}`,
  };
}

// ---- users CRUD (owner only) ----

export async function fetchUsers() {
  const res = await axios.get(`${API}/admin/users`, { headers: authHeaders() });
  return res.data;
}

export async function createUser(data) {
  const res = await axios.post(`${API}/admin/users`, data, { headers: authHeaders() });
  return res.data;
}

export async function updateUser(id, data) {
  const res = await axios.put(`${API}/admin/users/${id}`, data, { headers: authHeaders() });
  return res.data;
}

export async function deleteUser(id) {
  return axios.delete(`${API}/admin/users/${id}`, { headers: authHeaders() });
}

// ---- activity log ----

export async function fetchActivity(resourceType) {
  const params = resourceType ? { resource_type: resourceType } : {};
  const res = await axios.get(`${API}/admin/activity`, {
    headers: authHeaders(),
    params,
  });
  return res.data;
}

// ---- password reset ----

export async function requestPasswordReset(email) {
  const res = await axios.post(`${API}/admin/auth/forgot-password`, { email });
  return res.data;
}

export async function performPasswordReset(token, new_password) {
  const res = await axios.post(`${API}/admin/auth/reset-password`, {
    token,
    new_password,
  });
  return res.data;
}

// ---- tracking snippets CRUD ----

export async function fetchTrackingSnippets() {
  const res = await axios.get(`${API}/admin/tracking-snippets`, { headers: authHeaders() });
  return res.data;
}

export async function createTrackingSnippet(data) {
  const res = await axios.post(`${API}/admin/tracking-snippets`, data, { headers: authHeaders() });
  return res.data;
}

export async function updateTrackingSnippet(id, data) {
  const res = await axios.put(`${API}/admin/tracking-snippets/${id}`, data, { headers: authHeaders() });
  return res.data;
}

export async function deleteTrackingSnippet(id) {
  return axios.delete(`${API}/admin/tracking-snippets/${id}`, { headers: authHeaders() });
}

// ---- Page SEO ----

export async function fetchPageSeoList() {
  const res = await axios.get(`${API}/admin/page-seo`, { headers: authHeaders() });
  return res.data;
}

export async function upsertPageSeo(data) {
  const res = await axios.put(`${API}/admin/page-seo`, data, { headers: authHeaders() });
  return res.data;
}

export async function deletePageSeo(path) {
  return axios.delete(`${API}/admin/page-seo`, {
    params: { path },
    headers: authHeaders(),
  });
}

export function getSitemapUrl() {
  return `${API}/sitemap.xml`;
}
